import type { CampusBrickMinimal, CampusProductMinimal, GetUserCampusEventsResponse } from '@/types'

import apiClient from './apiClient'

const API_BASE_URL = '/api/campus'
const PRODUCTS_CACHE_TTL_MS = 5 * 60 * 1000

let productsCache: CampusProductMinimal[] | null = null
let productsCacheTimestamp = 0
let productsRequestPromise: Promise<CampusProductMinimal[]> | null = null

/**
 * Fetches campus events for the current user for a specific semester.
 * Events are categorized by source (own, role, unit).
 *
 * @param semesterId - The ID of the semester to fetch events for
 * @returns A promise that resolves to categorized campus events
 */
export async function getUserEvents(semesterId: number): Promise<GetUserCampusEventsResponse> {
  try {
    const response = await apiClient.get<GetUserCampusEventsResponse>(`${API_BASE_URL}/events`, {
      params: { semesterId },
    })
    return response.data
  } catch (error) {
    console.error('Error fetching campus events:', error)
    throw error
  }
}

/**
 * Fetches all available Campus products (study programs).
 *
 * @returns A promise that resolves to a list of campus products
 */
export async function getProducts(forceRefresh = false): Promise<CampusProductMinimal[]> {
  const now = Date.now()
  const hasFreshCache =
    !forceRefresh && productsCache !== null && now - productsCacheTimestamp < PRODUCTS_CACHE_TTL_MS

  if (hasFreshCache) {
    return productsCache!
  }

  if (!forceRefresh && productsRequestPromise) {
    return productsRequestPromise
  }

  productsRequestPromise = apiClient
    .get<CampusProductMinimal[]>(`${API_BASE_URL}/products`)
    .then((response) => {
      productsCache = response.data
      productsCacheTimestamp = Date.now()
      return response.data
    })
    .catch((error) => {
      console.error('Error fetching campus products:', error)
      throw error
    })
    .finally(() => {
      productsRequestPromise = null
    })

  return productsRequestPromise
}

/**
 * Searches for Campus bricks (modules) by search term.
 *
 * @param searchTerm - The search term to filter bricks
 * @returns A promise that resolves to a list of matching bricks
 */
export async function getBricks(searchTerm: string): Promise<CampusBrickMinimal[]> {
  try {
    const response = await apiClient.get<CampusBrickMinimal[]>(`${API_BASE_URL}/bricks`, {
      params: { searchTerm },
    })
    return response.data
  } catch (error) {
    console.error('Error fetching campus bricks:', error)
    throw error
  }
}

export default {
  getUserEvents,
  getProducts,
  getBricks,
}
